import { Download, ExternalLink, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../helpers/axiosConfig';
import { authHeaders, parseApiError } from '../../helpers/billingApi';
import { formatCurrency, formatDate } from '../../components/admin/adminUtils';
import { BILLING_OFFER_DETAIL, BILLING_OFFER_EYEBROW, HOSTED_YEAR_ONE } from '../../helpers/hostedOffer';
import { DEFAULT_INTERVAL, DEFAULT_PLAN_KEY } from '../../helpers/plans';
import PaymentMethods from '../../components/admin/PaymentMethods';
import ConfirmSubscribeModal from '../../components/admin/ConfirmSubscribeModal';

// Stripe invoice timestamps are Unix SECONDS — multiply by 1000 for Date.
const formatUnixSeconds = (seconds) => {
  if (!seconds && seconds !== 0) return 'Pending';
  return formatDate(new Date(seconds * 1000));
};

const intervalSuffix = (interval) => (interval === 'yearly' ? '/yr' : '/mo');

// Subscription status -> display badge. Trials are no longer used; the badge
// still degrades gracefully for any unexpected status.
const SUBSCRIPTION_STATUS = {
  active: { label: 'Active', className: 'active' },
  past_due: { label: 'Past due', className: 'past_due' },
  canceled: { label: 'Canceled', className: 'canceled' },
  disabled: { label: 'Disabled', className: 'canceled' },
  unpaid: { label: 'Unpaid', className: 'past_due' },
};

const subscriptionBadge = (status) =>
  SUBSCRIPTION_STATUS[status] || { label: status || 'Unknown', className: 'canceled' };

const CANCELABLE = new Set(['active', 'past_due']);
const isCanceled = (status) =>
  status === 'canceled' || status === 'disabled' || status === 'unpaid';

function CancelModal({ subscription, onConfirm, onClose, busy }) {
  if (!subscription) return null;
  const siteLabel = subscription.sites?.site_title || subscription.sites?.domain || 'this site';
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="create-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-sub-title">
        <header className="modal-head">
          <div className="modal-head-copy">
            <div className="modal-kicker">
              <span>CANCEL SUBSCRIPTION</span>
              {siteLabel}
            </div>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close cancel dialog">
            <X size={16} />
          </button>
        </header>
        <div className="modal-body">
          <h2>Cancel this subscription?</h2>
          <p>
            Your site stays online at its domain for <strong>30 days</strong>, then is scheduled for
            deletion. Resubscribe before then to keep it. This cannot be undone immediately.
          </p>
        </div>
        <footer className="modal-foot">
          <span>30-DAY GRACE PERIOD APPLIES</span>
          <div>
            <button className="btn btn-ghost" type="button" onClick={onClose} disabled={busy}>
              Keep subscription
            </button>
            <button className="btn btn-danger" type="button" onClick={onConfirm} disabled={busy}>
              {busy ? 'Canceling...' : 'Cancel subscription'}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}

export default function BillingPage() {
  const [billedSites, setBilledSites] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [resubTarget, setResubTarget] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const loadBilling = useCallback(async () => {
    setError('');
    try {
      const [billedResponse, invoicesResponse, subsResponse] = await Promise.all([
        axiosInstance.get('subscriptions/billed-sites', authHeaders()),
        axiosInstance.get('payments/invoices?limit=24', authHeaders()),
        axiosInstance.get('subscriptions', authHeaders()),
      ]);
      setBilledSites(Array.isArray(billedResponse.data) ? billedResponse.data : []);
      setInvoices(Array.isArray(invoicesResponse.data) ? invoicesResponse.data : []);
      setSubscriptions(Array.isArray(subsResponse.data) ? subsResponse.data : []);
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not load your billing details.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const monthlyTotal = useMemo(
    () =>
      billedSites.reduce(
        (sum, site) =>
          sum + (site.billing_interval === 'yearly' ? Math.round((site.amount || 0) / 12) : site.amount || 0),
        0,
      ),
    [billedSites],
  );

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setActionBusyId(cancelTarget.id);
    setActionError('');
    setActionMessage('');
    try {
      await axiosInstance.delete(`subscriptions/${cancelTarget.id}`, authHeaders());
      setActionMessage('Subscription canceled. Your site stays online during the 30-day grace period.');
      setCancelTarget(null);
      setLoading(true);
      await loadBilling();
    } catch (requestError) {
      setActionError(parseApiError(requestError, 'Could not cancel the subscription.'));
    } finally {
      setActionBusyId(null);
    }
  };

  // Open the confirm-and-pay modal for a canceled subscription.
  const startResubscribe = (subscription) => {
    setActionError('');
    setActionMessage('');
    if (!subscription.site_id) {
      setActionError('This subscription has no linked site to restore.');
      return;
    }
    // Preselect the plan the subscription was on. The modal falls back to the
    // first configured plan if that one is no longer in the catalog.
    setResubTarget({
      id: subscription.id,
      site_id: subscription.site_id,
      planKey: subscription.plan_name || DEFAULT_PLAN_KEY,
      interval: subscription.billing_interval || DEFAULT_INTERVAL,
    });
  };

  const confirmResubscribe = async (choice) => {
    if (!resubTarget) return;
    setActionBusyId(resubTarget.id);
    setActionError('');
    setActionMessage('');
    try {
      await axiosInstance.post(
        'subscriptions/',
        {
          site_id: resubTarget.site_id,
          plan_name: choice.planKey,
          billing_interval: choice.interval,
        },
        authHeaders(),
      );
      setActionMessage('Resubscribed. Your site has been restored.');
      setResubTarget(null);
      setLoading(true);
      await loadBilling();
    } catch (requestError) {
      setActionError(parseApiError(requestError, 'Could not resubscribe.'));
    } finally {
      setActionBusyId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Billing</h1>
          <div className="page-subtitle">Per-site subscriptions. Staging is free until a site goes live.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" type="button" onClick={loadBilling}>
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? <div className="loading-block">Loading billing</div> : null}
      {error ? <div className="error-banner admin-page-error">{error}</div> : null}
      {actionMessage ? <div className="success-banner admin-page-error">{actionMessage}</div> : null}
      {actionError ? <div className="error-banner admin-page-error">{actionError}</div> : null}

      {!loading ? (
        <div className="billing-layout">
          <section className="billing-main">
            <div className="billing-summary-grid">
              <div className="summary-tile wide">
                <span>Current monthly total</span>
                <strong>{formatCurrency(monthlyTotal)}/mo</strong>
                <p>{billedSites.length} live site{billedSites.length === 1 ? '' : 's'} - staging sites are free</p>
              </div>
              <div className="summary-tile">
                <span>{BILLING_OFFER_EYEBROW}</span>
                <strong>{HOSTED_YEAR_ONE}</strong>
                <p>{BILLING_OFFER_DETAIL}</p>
              </div>
            </div>

            {/* US-002: billed sites */}
            <section className="panel-card table-card">
              <div className="card-header">
                <h2>Sites being billed</h2>
              </div>
              <div className="admin-table billed-sites-table">
                <div className="admin-table-head">
                  <span>Site</span>
                  <span>Plan</span>
                  <span>Started</span>
                  <span>Amount</span>
                  <span>Renews</span>
                </div>
                {billedSites.length ? (
                  billedSites.map((site) => (
                    <div className="admin-table-row" key={site.subscription_id}>
                      <span>
                        <strong>{site.domain || 'Domain pending'}</strong>
                      </span>
                      <span>{site.plan_name || 'Hosted plan'}</span>
                      <span>{formatDate(site.started_at)}</span>
                      <span>
                        {formatCurrency(site.amount, site.currency)}
                        {intervalSuffix(site.billing_interval)}
                      </span>
                      <span>{formatDate(site.current_period_end)}</span>
                    </div>
                  ))
                ) : (
                  <div className="table-empty">You're not being billed for any sites yet.</div>
                )}
              </div>
            </section>

            {/* US-009: subscription management */}
            <section className="panel-card table-card">
              <div className="card-header">
                <h2>Subscriptions</h2>
              </div>
              <div className="admin-table billed-sites-table">
                <div className="admin-table-head">
                  <span>Site</span>
                  <span>Plan</span>
                  <span>Status</span>
                  <span>Renews</span>
                  <span />
                </div>
                {subscriptions.length ? (
                  subscriptions.map((sub) => {
                    const badge = subscriptionBadge(sub.status);
                    return (
                      <div className="admin-table-row" key={sub.id}>
                        <span>
                          <strong>{sub.sites?.site_title || 'Hosted site'}</strong>
                          <small>{sub.sites?.domain || 'Domain pending'}</small>
                        </span>
                        <span>
                          {sub.plan_name || 'Hosted plan'}
                          <small>
                            {formatCurrency(sub.amount, sub.currency)}
                            {intervalSuffix(sub.billing_interval)}
                          </small>
                        </span>
                        <span>
                          <span className={`invoice-status ${badge.className}`}>{badge.label}</span>
                          {sub.cancel_at_period_end ? <small>Ends at period end</small> : null}
                        </span>
                        <span>{formatDate(sub.current_period_end)}</span>
                        <span className="row-actions">
                          {CANCELABLE.has(sub.status) ? (
                            <button
                              className="btn btn-ghost btn-sm"
                              type="button"
                              onClick={() => setCancelTarget(sub)}
                              disabled={actionBusyId === sub.id}
                            >
                              Cancel
                            </button>
                          ) : null}
                          {isCanceled(sub.status) && sub.site_id ? (
                            <button
                              className="btn btn-primary btn-sm"
                              type="button"
                              onClick={() => startResubscribe(sub)}
                              disabled={actionBusyId === sub.id}
                            >
                              {actionBusyId === sub.id ? 'Restoring...' : 'Resubscribe'}
                            </button>
                          ) : null}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="table-empty">No subscriptions yet.</div>
                )}
              </div>
            </section>

            {/* US-003: invoice history */}
            <section className="panel-card table-card">
              <div className="card-header">
                <h2>Invoice history</h2>
              </div>
              <div className="admin-table invoices-table">
                <div className="admin-table-head">
                  <span>Date</span>
                  <span>Invoice #</span>
                  <span>Amount</span>
                  <span>Status</span>
                  <span />
                </div>
                {invoices.length ? (
                  invoices.map((invoice) => (
                    <div className="admin-table-row" key={invoice.id}>
                      <span>{formatUnixSeconds(invoice.created)}</span>
                      <span>{invoice.number || '—'}</span>
                      <span>{formatCurrency(invoice.amount_paid, invoice.currency)}</span>
                      <span className={`invoice-status ${invoice.status}`}>{invoice.status}</span>
                      <span className="row-actions">
                        {invoice.invoice_pdf ? (
                          <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" download>
                            <Download size={14} />
                            PDF
                          </a>
                        ) : null}
                        {invoice.hosted_invoice_url ? (
                          <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink size={14} />
                            View
                          </a>
                        ) : null}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="table-empty">No invoices yet.</div>
                )}
              </div>
            </section>
          </section>

          <aside className="billing-side">
            {/* US-004 + US-005 */}
            <PaymentMethods />

            <section className="panel-card card-pad">
              <h2>Billing contact</h2>
              <p>Invoices use your account email until a dedicated billing contact is added.</p>
              <button className="btn btn-ghost" type="button">Edit details</button>
            </section>
          </aside>
        </div>
      ) : null}

      <CancelModal
        subscription={cancelTarget}
        onConfirm={confirmCancel}
        onClose={() => setCancelTarget(null)}
        busy={actionBusyId === cancelTarget?.id}
      />

      <ConfirmSubscribeModal
        open={!!resubTarget}
        defaultPlanKey={resubTarget?.planKey}
        defaultInterval={resubTarget?.interval}
        onConfirm={confirmResubscribe}
        onClose={() => setResubTarget(null)}
        busy={actionBusyId === resubTarget?.id}
      />
    </>
  );
}
